import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { 
  Users, Award, Clock, Shield, Target, 
  Briefcase, CheckCircle2, ArrowRight
} from 'lucide-react';

const TeamSection = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      badge: 'NOTRE ÉQUIPE',
      title: 'Des experts à votre service',
      description: 'KEYAN EXPERTISE rassemble des professionnels qualifiés et passionnés, unis par un objectif commun : garantir votre satisfaction.',
      stats: [
        { icon: Briefcase, value: '6+', label: 'Domaines d\'expertise' },
        { icon: Users, value: '100%', label: 'Satisfaction client' },
        { icon: Clock, value: '24/7', label: 'Support disponible' },
        { icon: Award, value: 'Pro', label: 'Certifications' }
      ],
      commitments: [
        'Professionnels expérimentés et certifiés',
        'Accompagnement personnalisé',
        'Respect des normes et des délais',
        'Communication transparente'
      ],
      cta: 'Nous contacter'
    },
    en: {
      badge: 'OUR TEAM',
      title: 'Experts at your service',
      description: 'KEYAN EXPERTISE brings together qualified and passionate professionals, united by a common goal: ensuring your satisfaction.',
      stats: [
        { icon: Briefcase, value: '6+', label: 'Areas of expertise' },
        { icon: Users, value: '100%', label: 'Client satisfaction' },
        { icon: Clock, value: '24/7', label: 'Support available' },
        { icon: Award, value: 'Pro', label: 'Certifications' }
      ],
      commitments: [
        'Experienced and certified professionals',
        'Personalized support',
        'Compliance with standards and deadlines',
        'Transparent communication'
      ],
      cta: 'Contact us'
    }
  };

  const t = content[language];

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="team" className="py-24 bg-[#1A1A1A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A227]/20 border border-[#C9A227]/30 rounded-full mb-6">
            <Users className="w-4 h-4 text-[#C9A227]" />
            <span className="text-[#C9A227] font-semibold text-sm">{t.badge}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t.title}
          </h2>
          <p className="text-xl text-gray-400">
            {t.description}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {t.stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div 
                key={index}
                className="bg-[#2A2A2A] rounded-2xl p-6 text-center border border-[#3A3A3A] hover:border-[#C9A227]/30 transition-colors"
              >
                <div className="w-14 h-14 bg-[#C9A227] rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </div>
            );
          })}
        </div>

        {/* Commitments & CTA */}
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Commitments */}
          <div className="bg-[#2A2A2A] rounded-2xl p-8 border border-[#3A3A3A]">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-8 h-8 text-[#C9A227]" />
              <h3 className="text-2xl font-bold text-white">
                {language === 'fr' ? 'Notre engagement' : 'Our commitment'}
              </h3>
            </div>
            <div className="space-y-4">
              {t.commitments.map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#C9A227] flex-shrink-0" />
                  <span className="text-gray-300">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center md:text-left">
            <p className="text-gray-400 mb-8 text-lg">
              {language === 'fr' 
                ? "Vous avez un projet ? Notre équipe est prête à vous accompagner."
                : "Have a project? Our team is ready to assist you."}
            </p>
            <Button
              onClick={scrollToContact}
              size="lg"
              className="bg-[#C9A227] hover:bg-[#B8922A] text-white px-8 py-6 text-lg group"
            >
              {t.cta}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamSection;